import { IconCheck, IconClockDollar, IconFileUpload } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useConciliarCfdiContrapago } from "@/features/conciliar-cfdi-contrapago/hooks/use-conciliar-cfdi-contrapago";
import { ReglasRechazadasCard } from "@/features/cargar-cfdi/components/ReglasRechazadasCard";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { Alert, Badge, Button, Card, Group, PageHeader, Stack, Text, ThemeIcon } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import type { CargarCFDIResultado } from "@/lib/types";
import { formatFecha } from "@/shared/format/fecha";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/conciliar-cfdi";

/**
 * P44 · FB60 Contrapago · último paso: el CFDI real llega DESPUÉS del pago.
 * A diferencia de `CargarCFDIPage` (CFDI único), aquí solo se sube el XML
 * (el PDF ya no aplica como bloqueante · puede adjuntarse como Adicional si
 * se desea) y la validación es SÍNCRONA (RN1-RN12 + reglas de conciliación
 * corren en una sola llamada, incluyendo la consulta SAT · sin polling).
 */
export function ConciliarCfdiContrapagoPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const inputRef = useRef<HTMLInputElement>(null);
  const [resultado, setResultado] = useState<CargarCFDIResultado | null>(null);

  const esDueno = useEsDueno(sol);
  // Hotspots 86/87 · conciliar exige `contrapagos.gestionar` (endpoint) y ser el dueño (dominio).
  const tienePermiso = useUserPermisos().includes(PERMISOS.contrapagosGestionar);
  const yaConciliado = sol.esperaCfdiConciliadoAt !== null;
  const puedeSubir = esDueno && tienePermiso && sol.estado === "Autorizada" && !yaConciliado;

  const conciliar = useConciliarCfdiContrapago(id, {
    onReglasAutorizadas: (r) => setResultado(r),
    onReglasRechazadas: (r) => setResultado(r),
  });

  const onArchivo = (files: FileList | null) => {
    const xml = files?.[0];
    if (!xml) return;
    setResultado(null);
    conciliar.mutate(xml);
  };

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Conciliar CFDI"
        description="FB60 Contrapago · sube el CFDI real que llegó después del pago."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Conciliar CFDI" },
        ]}
      />
      <LayoutConStepper
        estado={sol.estado}
        subtipo={sol.subtipo}
        tipo={sol.tipo}
        prorrateoAutomatico={sol.prorrateoAutomatico}
        paso="conciliacion"
        solicitudId={id}
        contabilizada={sol.enviadaAContabilizacion}
        esperaCfdiConciliadoAt={sol.esperaCfdiConciliadoAt}
        fillHeight
      >
        <Stack gap="md">
          {yaConciliado ? (
            <Card padding="lg" style={{ borderColor: "var(--color-multiva-400)", background: "color-mix(in srgb, var(--color-multiva-600) 6%, transparent)" }}>
              <Group gap="sm">
                <ThemeIcon color="multiva" variant="filled" size="lg">
                  <IconCheck size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={600}>CFDI conciliado</Text>
                  <Text size="sm" c="dimmed">
                    El CFDI real ya se recibió y coincidió con lo capturado el {formatFecha(sol.esperaCfdiConciliadoAt)}.
                  </Text>
                </div>
              </Group>
            </Card>
          ) : (
            <>
              <Alert variant="info" icon={<IconClockDollar size={16} stroke={1.7} />}>
                Esta solicitud ya fue pagada y está en espera del CFDI real.
                {sol.esperaCfdiFechaLimite && (
                  <>
                    {" "}
                    Límite para conciliar: <strong>{formatFecha(sol.esperaCfdiFechaLimite)}</strong>.
                  </>
                )}
                {sol.esperaCfdiVencida && (
                  <Badge color="peligro" variant="filled" className="ml-2">
                    Plazo vencido
                  </Badge>
                )}
              </Alert>

              {puedeSubir && (
                <Card padding="lg" className="border-dashed" style={{ background: "color-mix(in srgb, var(--color-foreground) 5%, transparent)" }}>
                  <Stack align="center" gap="sm" className="py-5">
                    <ThemeIcon variant="light" color="multiva" size="xl">
                      <IconFileUpload size={24} stroke={1.6} />
                    </ThemeIcon>
                    <Text fw={600}>Sube el CFDI (XML) del proveedor</Text>
                    <Text size="sm" c="dimmed" ta="center">
                      Se validará contra las reglas RN1-RN12 y contra el proveedor, total y moneda capturados al inicio de la solicitud.
                    </Text>
                    <Button leftSection={<IconFileUpload size={14} />} loading={conciliar.isPending} onClick={() => inputRef.current?.click()}>
                      {conciliar.isPending ? "Validando…" : "Seleccionar XML"}
                    </Button>
                    {/* Oculto visualmente pero renderizado (no display:none) · algunos
                        navegadores no abren el diálogo nativo con .click() sobre un
                        input display:none. */}
                    <input
                      ref={inputRef}
                      type="file"
                      accept=".xml,application/xml,text/xml"
                      className="absolute h-px w-px overflow-hidden opacity-0"
                      style={{ pointerEvents: "none" }}
                      tabIndex={-1}
                      aria-hidden="true"
                      onChange={(e) => {
                        onArchivo(e.currentTarget.files);
                        e.currentTarget.value = "";
                      }}
                    />
                  </Stack>
                </Card>
              )}

              {resultado && !resultado.reglasAutorizadas && <ReglasRechazadasCard errores={resultado.erroresReglas} />}
            </>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => navigate({ to: "/solicitudes/$id", params: { id } })}>
              Ver detalle de la solicitud
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
